<template>
  <div class="contract-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="contract-container">
      <!-- Header Section -->
      <div class="contract-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-return-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-file-contract title-icon"></i>
              Contratos
            </h1>
            <p class="page-subtitle">
              Escreva contratos dinamicamente para seus residentes
            </p>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div v-if="resident" class="contract-content">
        <!-- Resident Information Card -->
        <div class="resident-card">
          <div class="resident-info">
            <div class="resident-avatar">
              <img
                v-if="resident.photo_url"
                :src="resident.photo_url"
                :alt="resident.name"
                class="avatar-image"
              />
              <div v-else class="avatar-placeholder">
                <i class="fas fa-user-circle"></i>
              </div>
            </div>
            <div class="resident-details">
              <h4 class="resident-name">{{ resident.name }}</h4>
            </div>
          </div>
        </div>

        <!-- Contract Variables Section -->
        <div class="variables-section">
          <div class="variables-header">
            <h3 class="section-title">
              <i class="fas fa-code mr-2"></i>
              Variáveis Disponíveis
            </h3>
            <p class="section-description">
              Clique nas variáveis abaixo para inserí-las no seu contrato
            </p>
          </div>
          <div class="variables-container">
            <div v-if="resident_id" class="variables-content">
              <ContractVariableList />
            </div>
            <div v-else class="no-resident-message">
              <i class="fas fa-exclamation-triangle"></i>
              <span>Residente não encontrado</span>
            </div>
          </div>
        </div>

        <!-- Contract Form Section -->
        <div class="contract-form-section">
          <div class="form-grid">
            <!-- Contract Name -->
            <div class="form-card">
              <div class="form-header">
                <h3 class="form-title">
                  <i class="fas fa-tag mr-2"></i>
                  Nome do Contrato
                </h3>
              </div>
              <div class="form-content">
                <LabelRequiredField label="Nome do contrato" />
                <div class="input-container">
                  <input
                    type="text"
                    v-model="name"
                    placeholder="Ex: Contrato de Hospedagem - Janeiro 2025"
                    class="modern-form-input"
                    maxlength="100"
                  />
                  <div class="input-counter">{{ name.length }}/100</div>
                </div>
              </div>
            </div>

            <!-- Contract Text -->
            <div class="form-card contract-text-card">
              <div class="form-header">
                <h3 class="form-title">
                  <i class="fas fa-file-alt mr-2"></i>
                  Conteúdo do Contrato
                </h3>
              </div>
              <div class="form-content">
                <div class="textarea-container">
                  <textarea
                    id="text"
                    v-model="text"
                    placeholder="Digite o conteúdo do seu contrato aqui...

Dica: Use as variáveis disponíveis para personalizar automaticamente o contrato com os dados do residente."
                    class="modern-textarea"
                    rows="12"
                    maxlength="5000"
                  ></textarea>
                  <div class="textarea-footer">
                    <div class="character-counter">
                      <span :class="{ 'counter-warning': text.length > 4500 }">
                        {{ text.length }}/5000 caracteres
                      </span>
                    </div>
                    <div class="textarea-tips">
                      <i class="fas fa-lightbulb mr-1"></i>
                      Use as variáveis acima para personalizar o contrato
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="errors.length > 0" class="errors-section">
          <ErrorsMessages :errors="errors" />
        </div>

        <!-- Action Buttons -->
        <div class="actions-section">
          <v-btn
            :disabled="loading"
            class="btn-save-contract"
            @click="create()"
          >
            <i class="fas fa-save mr-2"></i>
            <span>{{ loading ? "Salvando..." : "Salvar Contrato" }}</span>
          </v-btn>
        </div>
      </div>

      <!-- No Resident Found -->
      <div v-else class="no-resident-section">
        <div class="no-resident-card">
          <i class="fas fa-user-slash no-resident-icon"></i>
          <h3 class="no-resident-title">Residente não encontrado</h3>
          <p class="no-resident-text">
            Não foi possível carregar as informações do residente selecionado.
          </p>
          <v-btn class="btn-return-modern" @click="handleToBack">
            <i class="fas fa-arrow-left mr-2"></i>
            Voltar
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
    
<script>
import NavBar from "../templates/NavBar";
import LabelRequiredField from "../templates/Components/LabelRequiredField";
import ContractVariableList from "../templates/Contract/ContractVariableList";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import toast from "../lib/ToastNotification";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import api from "../services/api";
import routes from "../routes";

export default {
  name: "Customer",
  components: {
    NavBar,
    AuthBySessionExpired,
    LabelRequiredField,
    ErrorsMessages,
    ContractVariableList,
  },
  data() {
    return {
      loading: false,
      resident: [],
      contract_variables: [],
      errors: [],
      resident_id: null,
      name: "",
      text: "",
    };
  },
  async created() {
    if (this.$route.params.resident_id) {
      this.resident_id = this.$route.params.resident_id;
    }
    await this.getContractVariables();
    await this.getResidentById();
  },
  methods: {
    async getContractVariables() {
      try {
        this.loading = true;
        const response = await api.get("/contract/contract-variables");
        const { contract_variables } = response.data;
        this.contract_variables = contract_variables;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },
    async getResidentById() {
      try {
        this.loading = true;
        const { resident_id } = this;
        const response = await api.get("/rest-home/resident/path", {
          params: { id: resident_id },
        });
        const { resident, error } = response.data;
        if (!error) {
          this.resident = resident;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },
    async create() {
      try {
        this.loading = true;
        const { resident_id, name, text } = this;
        let params = {
          resident_id,
          name,
          text,
        };
        const response = await api.post("/contract", params);
        const { error, errors, message } = response.data;
        if (!error) {
          toast(message, "success");
          this.handleToBack();
        } else {
          this.errors = errors;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    handleToBack: () => routes.go(-1),
  },
};
</script>

<style scoped>
.contract-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.contract-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.contract-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.btn-return-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-return-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Content Layout */
.contract-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
}

/* Resident Card */
.resident-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.resident-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.card-title i {
  color: #0f65b0;
}

.resident-info {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 24px;
}

.avatar-image {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: contain;
  border: 3px solid #0f65b0;
  box-shadow: 0 4px 12px rgba(15, 101, 176, 0.2);
}

.avatar-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #cbd5e1;
}

.avatar-placeholder i {
  font-size: 40px;
  color: #94a3b8;
}

.resident-details {
  flex: 1;
}

.resident-name {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.resident-label {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Variables Section */
.variables-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.variables-header {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  padding: 24px;
  border-bottom: 1px solid #f59e0b;
  text-align: center;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.section-title i {
  color: #d97706;
}

.section-description {
  color: #92400e;
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.variables-container {
  padding: 24px;
}

.variables-content {
  min-height: 120px;
}

.no-resident-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #64748b;
  font-size: 16px;
}

.no-resident-message i {
  color: #f59e0b;
  font-size: 20px;
}

/* Form Section */
.contract-form-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-grid {
  display: grid;
  gap: 24px;
}

.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.contract-text-card {
  grid-column: 1 / -1;
}

.form-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.form-title i {
  color: #0f65b0;
}

.form-content {
  padding: 24px;
}

/* Input Styles */
.input-container {
  position: relative;
  margin-top: 8px;
}

.modern-form-input {
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  background: white;
  transition: all 0.3s ease;
  font-family: "WorkSans-Regular", sans-serif;
}

.modern-form-input:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.input-counter {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
}

/* Textarea Styles */
.textarea-container {
  margin-top: 8px;
}

.modern-textarea {
  width: 100%;
  padding: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.6;
  font-family: "WorkSans-Regular", sans-serif;
  background: white;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 300px;
}

.modern-textarea:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.textarea-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.character-counter {
  font-size: 12px;
  font-weight: 500;
}

.character-counter span {
  color: #64748b;
}

.counter-warning {
  color: #f59e0b !important;
}

.textarea-tips {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.textarea-tips i {
  color: #f59e0b;
}

/* Error Section */
.errors-section {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.1);
}

/* Actions Section */
.actions-section {
  display: flex;
  justify-content: flex-end;
  padding: 24px 0;
}

.btn-save-contract {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 32px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  font-size: 14px !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-save-contract:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

.btn-save-contract:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* No Resident Section */
.no-resident-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.no-resident-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 48px;
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.no-resident-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 24px;
}

.no-resident-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.no-resident-text {
  color: #64748b;
  font-size: 16px;
  margin: 0 0 32px 0;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .contract-container {
    padding: 0 16px;
  }

  .contract-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .contract-content {
    gap: 20px;
  }

  .resident-info {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .avatar-image,
  .avatar-placeholder {
    width: 64px;
    height: 64px;
  }

  .avatar-placeholder i {
    font-size: 32px;
  }

  .resident-name {
    font-size: 20px;
  }

  .variables-header,
  .form-content {
    padding: 20px 16px;
  }

  .textarea-footer {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
    text-align: center;
  }

  .actions-section {
    justify-content: center;
  }

  .no-resident-card {
    padding: 32px 24px;
  }

  .no-resident-icon {
    font-size: 48px;
  }

  .no-resident-title {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .btn-return-modern,
  .btn-save-contract {
    padding: 12px 20px !important;
    font-size: 13px !important;
  }

  .page-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .modern-form-input,
  .modern-textarea {
    padding: 14px 16px;
    font-size: 14px;
  }

  .card-title,
  .form-title,
  .section-title {
    font-size: 16px;
  }

  .modern-textarea {
    min-height: 200px;
  }
}
</style>